/* eslint-disable react/require-default-props */
import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { addMinutes } from 'date-fns';
import {
  AppointmentType,
  AppointmentType as DBAppointmentType,
} from '@prisma/client';
import { Appointment, GeneralRequest } from '../../interfaces';
import './form.css';
import {
  CreateAptValues,
  DEFAULT_APT_LENGTH,
  DEFAULT_CONSULT_LENGTH,
  ImageItem,
  getExistingImgList,
  initFormValues,
  validationSchema,
} from '../../utils/form/appointmentForm';
import {
  createAppointment,
  invalidateAvailability,
  invalidateDepositTransferAppointments,
  markDepositPaid,
  scheduleRequest,
  updateAppointment,
} from '../../services/appointments';
import AppointmentTypeComponent from './AppointmentType';
import ClientSearchAutofill from './ClientSearchAutofill';
import AppointmentDetails from './AppointmentDetails';
import TattooDetails from './TattooDetails';
import { addCustomerAlgolia, getRoleByPhoneNumber } from '../../services/user';
import InternalNotes from './InternalNotes';
import { AppContext } from '../../context/AppContext/context';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import useConfirm from '../../hooks/useConfirm';
import { isUserEmployeeOrAdmin } from '../../utils/global/helper';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import {
  getAppointmentFormErrorMessage,
  getCrossDatelineError,
  getWalkInStartDateTime,
  isAppointmentDepositPaid,
  isWaiverSelectable,
  validatePrice,
} from '../../helpers/appointments';
import { DEFAULT_TIMEZONE } from '../../constants/appointments';
import useWaiverRequestsTableActions from '../../hooks/waivers/useWaiverRequestsTableActions';
import {
  cancelWaiverRequest,
  reactivateWaiverRequest,
  requestWaiver,
} from '../../services/waivers';
import { isAppointmentWaiverUpdatable } from '../../helpers/waivers';
import useUser from '../../hooks/global/useUser';
import useGetWaivers from '../../hooks/waivers/useGetWaivers';
import AppointmentDate from './AppointmentDate';

export type CreateUpdateArgs =
  | {
      type: 'update';
      appointment: Appointment | GeneralRequest;
      newAptId?: string;
    }
  | {
      type: 'create';
      newAptId?: string;
    };

interface Props {
  selectedRequest?: Appointment | GeneralRequest;
  onClose: () => void;
  onCreateUpdateSuccess?: (args: CreateUpdateArgs) => void | Promise<void>;
  setShouldScrollToTop?: React.Dispatch<React.SetStateAction<boolean>>;
  appointmentType?: DBAppointmentType;
}

export default function AppointmentForm({
  selectedRequest,
  onClose,
  onCreateUpdateSuccess,
  setShouldScrollToTop,
  appointmentType: initialAppointmentType,
}: Props) {
  const confirm = useConfirm();
  const { user: loadedUser } = useUser();
  const { selectedAppointment, setSelectedAppointmentUpdated } =
    useCalendarSelectedAppointment();
  const { updateLoadingModal, closeLoadingModal } = useLoadingModal();
  const { data: initialWaiverData } = useGetWaivers();

  const canManageAppointment = useFeatureGuard(Feature.MANAGE_APPOINTMENT);
  const canAddAppointment = useFeatureGuard(Feature.ADD_APPOINTMENT);

  const [formError, setFormError] = React.useState<any>('');

  const [images, setImages] = React.useState<ImageItem[]>([]);

  const { state } = React.useContext(AppContext);
  const { piercingLocations } = state;

  const { resetWaiversPage } = useWaiverRequestsTableActions();

  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);
  // do not want to run useEffect on initialWaiverData change
  const [waiverData] = React.useState(initialWaiverData);

  const firstAppointmentType = (initialAppointmentType ||
    initFormValues.appointmentType) as AppointmentType;
  const isInitialConsultation = firstAppointmentType === 'CONSULTATION';
  const isInitialWalkIn = firstAppointmentType === 'WALK_IN';
  const isInitialConsultationOrWalkIn =
    isInitialConsultation || isInitialWalkIn;

  const [loadedInitValues, setLoadedInitValues] =
    React.useState<CreateAptValues>({
      ...initFormValues,
      appointmentType: firstAppointmentType,
      depositPrice: isInitialConsultationOrWalkIn
        ? 0
        : loadedUser?.business.defaultTattooDeposit || 0,
      price: isInitialConsultation
        ? 0
        : loadedUser?.business.defaultTattooDeposit || 0,
      requestWaiver: isWaiverSelectable({
        waiverData,
        hasWaiversFeature,
        appointmentType: firstAppointmentType,
      }),
      appointmentLengthInMinutes: isInitialConsultation
        ? DEFAULT_CONSULT_LENGTH
        : DEFAULT_APT_LENGTH,
      depositPaid: false,
    });

  React.useEffect(() => {
    if (selectedAppointment || selectedRequest) {
      const tempNewInitValues: CreateAptValues = { ...initFormValues };

      const existingAppointment = (selectedAppointment ||
        selectedRequest) as Appointment;
      const appointmentType = selectedRequest
        ? initialAppointmentType || 'NEW_APPOINTMENT'
        : existingAppointment.appointmentType;
      const isWalkIn = appointmentType === 'WALK_IN';
      const isConsultation = appointmentType === 'CONSULTATION';
      const isWalkInOrConsultation = isWalkIn || isConsultation;
      const defaultLength = isConsultation
        ? DEFAULT_CONSULT_LENGTH
        : DEFAULT_APT_LENGTH;

      const showWaiverSelect = isWaiverSelectable({
        waiverData,
        hasWaiversFeature,
        appointmentType,
      });

      tempNewInitValues.businessTripId =
        existingAppointment.businessTripId || '';
      tempNewInitValues.appointmentType = appointmentType;
      tempNewInitValues.appointmentLengthInMinutes = String(
        selectedRequest
          ? existingAppointment.appointmentLengthInMinutes || defaultLength
          : existingAppointment.appointmentLengthInMinutes || '',
      );
      tempNewInitValues.employeeId = existingAppointment.employeeId || '';
      tempNewInitValues.customerId = existingAppointment.customerId;
      tempNewInitValues.depositPrice = existingAppointment.depositPrice || 0;
      if (existingAppointment.endDateTime) {
        tempNewInitValues.endDateTime = existingAppointment.endDateTime;
      }
      if (existingAppointment.startDateTime) {
        tempNewInitValues.startDateTime = existingAppointment.startDateTime;
      }
      if (existingAppointment.priceType) {
        tempNewInitValues.priceType = existingAppointment.priceType;
      }
      tempNewInitValues.tattooColor = existingAppointment.tattooColor || '';
      tempNewInitValues.tattooDetails = existingAppointment.tattooDetails || '';
      tempNewInitValues.tattooLocation =
        existingAppointment.tattooLocation || '';
      tempNewInitValues.tattooSize = existingAppointment.tattooSize || '';

      tempNewInitValues.serviceId = existingAppointment.serviceId
        ? String(existingAppointment.serviceId)
        : '0';

      tempNewInitValues.piercingLocationId =
        existingAppointment.piercingLocationId
          ? String(existingAppointment.piercingLocationId)
          : '';

      if (existingAppointment.piercingLocationId) {
        tempNewInitValues.locationCategory =
          piercingLocations?.find(
            location =>
              location.locationId === existingAppointment.piercingLocationId,
          )?.locationCategory || '';
      }

      if (tempNewInitValues.priceType === 'FIXED') {
        tempNewInitValues.price = existingAppointment.price;
      } else if (tempNewInitValues.priceType === 'RANGE') {
        tempNewInitValues.minPrice = existingAppointment.minPrice;
        tempNewInitValues.maxPrice = existingAppointment.maxPrice;
      } else {
        tempNewInitValues.hourlyPrice = existingAppointment.hourlyPrice;
      }

      if (loadedUser?.business.defaultTattooDeposit && !selectedAppointment) {
        if (!isWalkInOrConsultation) {
          tempNewInitValues.depositPrice =
            loadedUser.business.defaultTattooDeposit;
        }
        if (!isConsultation) {
          tempNewInitValues.price = loadedUser.business.defaultTattooDeposit;
        }
      }

      tempNewInitValues.internalNotes = existingAppointment.internalNotes;

      tempNewInitValues.firstName = existingAppointment.customer.firstName;
      tempNewInitValues.lastName = existingAppointment.customer.lastName;
      tempNewInitValues.email = existingAppointment.customer.email;
      tempNewInitValues.phoneNumber =
        existingAppointment.customer.phoneNumber.replace('+', '');
      tempNewInitValues.dob = existingAppointment.customer.dob?.split('T')[0];
      tempNewInitValues.requestWaiver = selectedAppointment
        ? !!selectedAppointment.waiverRequest &&
          selectedAppointment.waiverRequest.status !== 'CANCELED'
        : !isConsultation && showWaiverSelect;

      if (existingAppointment.refImgsId) {
        setImages(getExistingImgList(existingAppointment));
      }

      if (isConsultation && !existingAppointment.onschedId) {
        tempNewInitValues.allowOverlapping = true;
      }

      if (isConsultation) {
        tempNewInitValues.priceType = 'FIXED';
        tempNewInitValues.depositPrice = 0;
        tempNewInitValues.price = 0;
      }

      tempNewInitValues.depositPaid =
        isAppointmentDepositPaid(existingAppointment);

      setLoadedInitValues(tempNewInitValues);
    }
  }, [
    selectedAppointment,
    piercingLocations,
    selectedRequest,
    initialAppointmentType,
    waiverData,
    hasWaiversFeature,
    loadedUser?.business.defaultTattooDeposit,
  ]);

  const handleUpdateWaiverRequest = async (
    values: CreateAptValues,
    appointment: Appointment,
  ) => {
    if (!isAppointmentWaiverUpdatable(appointment.startDateTime)) return;

    if (values.requestWaiver) {
      if (!appointment.waiverRequest) {
        await requestWaiver({
          appointment,
        });
      } else if (appointment.waiverRequest?.status === 'CANCELED') {
        await reactivateWaiverRequest({
          businessId: loadedUser?.business.id || '',
          waiverRequestId: appointment.waiverRequest.id,
        });
      }
    } else if (appointment.waiverRequest) {
      await cancelWaiverRequest({
        businessId: loadedUser?.business.id || '',
        waiverRequestId: appointment.waiverRequest.id,
      });
    }
  };

  const onSubmitSuccess = async (
    args: CreateUpdateArgs & {
      values: CreateAptValues;
    },
  ) => {
    const appointmentId =
      args.newAptId ||
      ('appointment' in args && 'appointmentId' in args.appointment
        ? args.appointment.appointmentId
        : undefined);

    if (
      appointmentId &&
      args.values.appointmentType === 'NEW_APPOINTMENT' &&
      args.values.depositPaid &&
      (args.values.depositPrice ?? 0) > 0 &&
      (!selectedAppointment || !isAppointmentDepositPaid(selectedAppointment))
    ) {
      await markDepositPaid(appointmentId, {
        depositPaymentMethod: 'CASH',
      }).catch(() => {
        // Do not throw error if deposit is not marked as paid
      });
    }

    await onCreateUpdateSuccess?.(args);
  };

  const updateExistingAppointment = async ({
    values,
    newImages,
    imagesToDelete,
  }: {
    values: CreateAptValues;
    newImages: ImageItem[];
    imagesToDelete: string[];
  }) => {
    if (!selectedAppointment) return;
    try {
      const payload: Parameters<typeof updateAppointment>[0] = {
        ...values,
        newImages,
        imagesToDelete,
      };

      const updateAppointmentResponse = await updateAppointment(
        payload,
        selectedAppointment,
      );

      if (updateAppointmentResponse) {
        try {
          const aptCustomerId =
            updateAppointmentResponse.data.result.customerId;
          await addCustomerAlgolia(
            aptCustomerId,
            loadedUser?.business.id || '',
          );

          await handleUpdateWaiverRequest(values, selectedAppointment);
        } catch {
          // Do nothing
        }

        toast.success('Appointment Updated!');
        invalidateDepositTransferAppointments();
        setSelectedAppointmentUpdated(true);

        onClose();
        await onSubmitSuccess({
          type: 'update',
          appointment: selectedAppointment,
          values,
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        const message = getAppointmentFormErrorMessage(error);
        setFormError(message);
      } else {
        setFormError('An unexpected error occurred.'); // Some generic message
      }
      toast.error('Error updating appointment');
      if (setShouldScrollToTop) {
        setShouldScrollToTop(true);
      }
    }
  };

  const scheduleApptFromRequest = async ({
    values,
    newImages,
    imagesToDelete,
  }: {
    values: CreateAptValues;
    newImages: ImageItem[];
    imagesToDelete: string[];
  }) => {
    if (!selectedRequest) return;
    if (!values.firstName || !values.phoneNumber) return;
    try {
      const updateAppointmentResponse = await scheduleRequest(
        {
          ...values,
          imagesToDelete,
          newImages,
          businessId: loadedUser?.business.id || '',
        },
        selectedRequest,
      );

      if (updateAppointmentResponse) {
        try {
          const aptCustomerId =
            updateAppointmentResponse.data.result.customerId;
          await addCustomerAlgolia(
            aptCustomerId,
            loadedUser?.business.id || '',
          );
        } catch (error) {
          // Do something with the error
        }

        toast.success('Appointment Updated!');
        setSelectedAppointmentUpdated(true);

        const newAptId = updateAppointmentResponse.data.result.appointmentId;

        onClose();
        await onSubmitSuccess({
          type: 'update',
          appointment: selectedRequest,
          newAptId, // Send new Apt. ID in case we're scheduling a general request
          values,
        });

        if (values.requestWaiver) {
          resetWaiversPage();
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        const message = getAppointmentFormErrorMessage(error);
        setFormError(message);
      } else {
        setFormError('An unexpected error occurred.'); // Some generic message
      }
      toast.error('Error updating appointment');
      if (setShouldScrollToTop) {
        setShouldScrollToTop(true);
      }
    }
  };

  const createNewAppointment = async ({
    values,
    newImages,
  }: {
    values: CreateAptValues;
    newImages: ImageItem[];
  }) => {
    if (!values.firstName || !values.phoneNumber || !values.appointmentType)
      throw new Error('Please enter all client details and try again');

    try {
      const phoneNumber = !values.phoneNumber.startsWith('+')
        ? `+${values.phoneNumber}`
        : values.phoneNumber;
      const userRole = await getRoleByPhoneNumber(phoneNumber);
      const requireConfirmation = userRole && isUserEmployeeOrAdmin(userRole);
      if (requireConfirmation) {
        const accepted = await confirm({
          message:
            "You're scheduling with an artist. The client information you entered will not overwrite the existing client information. Are you sure you want to continue?",
          title: 'Scheduling with artist',
        });
        if (!accepted) return;
      }

      const appointmentResponse = await createAppointment({
        ...values,
        newImages,
        businessId: loadedUser?.business.id || '',
      });

      if (appointmentResponse) {
        try {
          const aptCustomerId = appointmentResponse.data?.result?.customerId;

          await addCustomerAlgolia(
            aptCustomerId,
            loadedUser?.business.id || '',
          );
        } catch (error) {
          // Do something with the error
        }

        toast.success('Appointment Created!');
        onClose();
        await onSubmitSuccess({
          type: 'create',
          newAptId: appointmentResponse.data.result.appointmentId,
          values,
        });
        if (values.requestWaiver) {
          resetWaiversPage();
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        const message = getAppointmentFormErrorMessage(error);
        setFormError(message);
      } else {
        setFormError('An unexpected error occurred.'); // Some generic message
      }
      toast.error('Error creating appointment');
      if (setShouldScrollToTop) {
        setShouldScrollToTop(true);
      }
    } finally {
      closeLoadingModal();
    }
  };

  return (
    <Box
      style={{
        width: 900,
        margin: 'auto',
      }}>
      <Formik
        enableReinitialize
        initialValues={loadedInitValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          if (!canAddAppointment && !canManageAppointment) return;

          setFormError('');

          const newImages = images.filter(i => !i.isUploaded);
          const existingImages =
            selectedAppointment?.refImgsId ?? selectedRequest?.refImgsId ?? [];
          const imagesToDelete = existingImages.filter(
            img => !images.some(i => i.url === img),
          );

          // if time goes into next day, e.g. 11pm to 1am
          const startDateTime =
            values.appointmentType === 'WALK_IN'
              ? getWalkInStartDateTime()
              : values.startDateTime;
          const endDate = addMinutes(
            new Date(startDateTime),
            parseInt(values.appointmentLengthInMinutes || '0', 10),
          );

          const employee = loadedUser?.business.employees.find(
            e => e.employeeId === values.employeeId,
          );
          if (!employee) {
            setFormError('Employee not found');
            return;
          }

          const dateError = getCrossDatelineError({
            startDateTime,
            endDateTime: endDate,
            additionalAppointments: values.additionalAppointments,
            appointment: selectedAppointment,
            timezone: employee.user?.timezone || DEFAULT_TIMEZONE,
          });

          if (dateError) {
            const accepted = await confirm(dateError);
            if (!accepted) return;
          }

          const priceError = validatePrice(
            values,
            values.appointmentType as AppointmentType,
          );
          if (priceError) {
            setFormError(priceError.error);
            return;
          }

          const isConsultation = values.appointmentType === 'CONSULTATION';

          updateLoadingModal({
            header: selectedAppointment
              ? 'Updating Appointment...'
              : isConsultation
              ? 'Scheduling Consultation...'
              : 'Creating Appointment...',
            message: 'This could take a minute, please do not refresh the page',
            status: 'loading',
          });

          if (selectedAppointment) {
            await updateExistingAppointment({
              values,
              newImages,
              imagesToDelete,
            });
          } else if (selectedRequest) {
            await scheduleApptFromRequest({
              values,
              newImages,
              imagesToDelete,
            });
          } else if (
            values.firstName &&
            values.phoneNumber &&
            values.appointmentType
          ) {
            await createNewAppointment({
              values,
              newImages,
            });
          } else {
            setFormError('Please enter all client details and try again');
          }

          invalidateAvailability();

          setSubmitting(false);
          closeLoadingModal();
        }}>
        {({ isSubmitting, setFieldValue, values, isValid }) => (
          <>
            {!selectedAppointment &&
              !selectedRequest &&
              !values.appointmentType && (
                <AppointmentTypeComponent
                  value={values.appointmentType as DBAppointmentType}
                  setFieldValue={value =>
                    setFieldValue('appointmentType', value)
                  }
                />
              )}

            {(selectedAppointment || values.appointmentType) && (
              <>
                <ClientSearchAutofill selectedRequest={selectedRequest} />
                <Form>
                  {formError && (
                    <Typography variant="body1" color="primary">
                      {formError}
                    </Typography>
                  )}
                  <AppointmentDetails />

                  <AppointmentDate />

                  <TattooDetails images={images} setImages={setImages} />
                  <InternalNotes />
                  {!selectedAppointment &&
                  values.appointmentType === 'WALK_IN' ? (
                    <Button
                      type="submit"
                      disabled={
                        isSubmitting ||
                        !isValid ||
                        !values.employeeId ||
                        !values.phoneNumber ||
                        !values.firstName ||
                        !values.serviceId ||
                        !values.appointmentLengthInMinutes
                      }
                      variant="contained"
                      className="primary">
                      Add Walk In
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={
                        isSubmitting ||
                        !isValid ||
                        !values.appointmentLengthInMinutes ||
                        !values.employeeId ||
                        !values.startDateTime ||
                        !values.firstName ||
                        !values.phoneNumber ||
                        !values.serviceId ||
                        (!canAddAppointment && !canManageAppointment)
                      }
                      variant="contained"
                      className="primary">
                      {selectedAppointment
                        ? 'Save Changes'
                        : values.appointmentType === 'CONSULTATION'
                        ? 'Schedule Consultation'
                        : 'Schedule Appointment'}
                    </Button>
                  )}
                </Form>
              </>
            )}
          </>
        )}
      </Formik>
    </Box>
  );
}
